"use client";
//
// Shared filter input components used by FilterBar and TableFilters.
// Pure functions (resolveFilterType, buildFilterClause, formatFilterClauseLabel,
// applyFilterClauseToRow, OPERATOR_SYMBOLS) live in ../app/filter-clause.ts.

import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import type { SelectList, SelectListItem, TextOperator } from "../domain";
import { FILTER_TYPE_OBJ, TEXT_OPERATOR_OBJ } from "../domain";
import { getListItemLabel, OPERATOR_LABELS, SELECT_OPERATOR_I18N_KEYS, SELECT_OPERATORS, type SelectFilterOperator } from "../app";
import { getColumnName, getListForColumn, resolveFilterType } from "./column-helpers";
import { useTableLabelFn } from "./labels-context";
import { useTableComponents, useTableConfig } from "./table-provider";

function isTextOperator(value: string): value is TextOperator {
  return value in OPERATOR_LABELS;
}

function isSelectFilterOperator(value: string): value is SelectFilterOperator {
  return SELECT_OPERATORS.some((operator) => operator === value);
}

export interface FilterInputProps<TData> {
  column: ColumnDef<TData> | undefined;
  lists?: SelectList[] | undefined;
  draftValue: string;
  setDraftValue: (v: string) => void;
  draftOperator: TextOperator;
  setDraftOperator: (v: TextOperator) => void;
  draftMin: string;
  setDraftMin: (v: string) => void;
  draftMax: string;
  setDraftMax: (v: string) => void;
  draftFrom: string;
  setDraftFrom: (v: string) => void;
  draftTo: string;
  setDraftTo: (v: string) => void;
  draftSelect: string;
  setDraftSelect: (v: string) => void;
  draftBool: boolean;
  setDraftBool: (v: boolean) => void;
  draftMultiValues: string[];
  setDraftMultiValues: (v: string[]) => void;
  draftSelectOperator: SelectFilterOperator;
  setDraftSelectOperator: (v: SelectFilterOperator) => void;
  onAdd: () => void;
}

export function FilterInput<TData>({
  column,
  lists,
  draftValue,
  setDraftValue,
  draftOperator,
  setDraftOperator,
  draftMin,
  setDraftMin,
  draftMax,
  setDraftMax,
  draftFrom,
  setDraftFrom,
  draftTo,
  setDraftTo,
  draftSelect,
  setDraftSelect,
  draftBool,
  setDraftBool,
  draftMultiValues,
  setDraftMultiValues,
  draftSelectOperator,
  setDraftSelectOperator,
  onAdd,
}: FilterInputProps<TData>) {
  const t = useTableLabelFn();
  const { Select } = useTableComponents();
  const { locale } = useTableConfig();
  if (!column) return null;
  const type = resolveFilterType(column, lists);
  const meta = column.meta;
  const listItems = getListForColumn(lists, column);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onAdd();
    }
  };
  switch (type) {
    case FILTER_TYPE_OBJ.TEXT: {
      const suggestions =
        listItems?.map((it) => it.value) ?? meta?.selectOptions ?? [];
      return (
        <div className="flex items-center gap-2">
          <Select
            size="sm"
            value={draftOperator}
            onChange={(v) => {
              if (isTextOperator(v)) setDraftOperator(v);
            }}
            ariaLabel={t("operatorAria")}
            className="w-auto min-w-[120px]"
            options={Object.entries(OPERATOR_LABELS).map(([value, label]) => ({
              value,
              label,
            }))}
          />
          <input
            type="text"
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("table.filters.contains")}
            aria-label={t("table.filters.aria.text")}
            list={`autocomplete-${getColumnName(column)}`}
            className="rounded-md border px-2 py-1 text-sm"
          />
          <datalist id={`autocomplete-${getColumnName(column)}`}>
            {suggestions.map((opt) => (
              <option key={opt} value={opt} />
            ))}
          </datalist>
        </div>
      );
    }
    case FILTER_TYPE_OBJ.NUMBER_RANGE:
      return (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={draftMin}
            onChange={(e) => setDraftMin(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("table.filters.min")}
            aria-label={t("table.filters.aria.min")}
            className="rounded-md border px-2 py-1 text-sm"
          />
          <span>-</span>
          <input
            type="number"
            value={draftMax}
            onChange={(e) => setDraftMax(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("table.filters.max")}
            aria-label={t("table.filters.aria.max")}
            className="rounded-md border px-2 py-1 text-sm"
          />
        </div>
      );
    case FILTER_TYPE_OBJ.DATE_RANGE:
      return (
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={draftFrom}
            onChange={(e) => setDraftFrom(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label={t("table.filters.aria.from")}
            className="rounded-md border px-2 py-1 text-sm"
          />
          <span>-</span>
          <input
            type="date"
            value={draftTo}
            onChange={(e) => setDraftTo(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label={t("table.filters.aria.to")}
            className="rounded-md border px-2 py-1 text-sm"
          />
        </div>
      );
    case FILTER_TYPE_OBJ.SELECT_SINGLE:
    case FILTER_TYPE_OBJ.SELECT_MULTI: {
      const listOptions =
        listItems?.map((it) => ({
          value: it.value,
          label: getListItemLabel(listItems, it.value, locale) ?? it.value,
        })) ??
        (meta?.selectOptions ?? []).map((opt) => ({
          value: opt,
          label: opt,
        }));
      // For empty/not-empty operators, no value input is needed
      if (
        draftSelectOperator === "is-empty" ||
        draftSelectOperator === "is-not-empty"
      ) {
        return (
          <div className="flex items-center gap-2">
            <Select
              size="sm"
              value={draftSelectOperator}
              onChange={(v) =>
                isSelectFilterOperator(v) && setDraftSelectOperator(v)
              }
              ariaLabel={t("selectOperatorAria")}
              className="w-auto min-w-[120px]"
              options={SELECT_OPERATORS.map((op) => ({
                value: op,
                label: t(SELECT_OPERATOR_I18N_KEYS[op]),
              }))}
            />
            <p className="text-sm text-text-secondary">
              {draftSelectOperator === "is-empty"
                ? t("filterEmptyHint")
                : t("filterNonEmptyHint")}
            </p>
          </div>
        );
      }
      // For equals/not-equals вЂ” single select
      if (
        draftSelectOperator === TEXT_OPERATOR_OBJ.EQUALS ||
        draftSelectOperator === TEXT_OPERATOR_OBJ.NOT_EQUALS
      ) {
        return (
          <div className="flex items-center gap-2">
            <Select
              size="sm"
              value={draftSelectOperator}
              onChange={(v) =>
                isSelectFilterOperator(v) && setDraftSelectOperator(v)
              }
              ariaLabel={t("selectOperatorAria")}
              className="w-auto min-w-[120px]"
              options={SELECT_OPERATORS.map((op) => ({
                value: op,
                label: t(SELECT_OPERATOR_I18N_KEYS[op]),
              }))}
            />
            <Select
              size="sm"
              value={draftSelect}
              onChange={(v) => setDraftSelect(v)}
              ariaLabel={t("table.filters.aria.select")}
              className="w-auto min-w-[120px]"
              options={[
                { value: "", label: t("table.filters.select") },
                ...listOptions,
              ]}
            />
          </div>
        );
      }
      // For in/not-in вЂ” multi select
      return (
        <div className="flex items-center gap-2">
          <Select
            size="sm"
            value={draftSelectOperator}
            onChange={(v) =>
              isSelectFilterOperator(v) && setDraftSelectOperator(v)
            }
            ariaLabel={t("selectOperatorAria")}
            className="w-auto min-w-[120px]"
            options={SELECT_OPERATORS.map((op) => ({
              value: op,
              label: t(SELECT_OPERATOR_I18N_KEYS[op]),
            }))}
          />
          <MultiSelectInput
            options={meta?.selectOptions ?? []}
            listItems={listItems}
            selected={draftMultiValues}
            onChange={setDraftMultiValues}
            onAdd={onAdd}
          />
        </div>
      );
    }
    case FILTER_TYPE_OBJ.BOOLEAN:
      return (
        <Select
          size="sm"
          value={String(draftBool)}
          onChange={(v) => setDraftBool(v === "true")}
          ariaLabel={t("table.filters.aria.boolean")}
          className="w-auto min-w-[100px]"
          options={[
            { value: "true", label: t("table.filters.true") },
            { value: "false", label: t("table.filters.false") },
          ]}
        />
      );
    case FILTER_TYPE_OBJ.EMPTY:
    case FILTER_TYPE_OBJ.NOT_EMPTY:
      return (
        <p className="text-sm text-text-secondary">
          {type === FILTER_TYPE_OBJ.EMPTY
            ? t("filterEmptyHint")
            : t("filterNonEmptyHint")}
        </p>
      );
    default:
      return null;
  }
}

export interface MultiSelectInputProps {
  options: string[];
  listItems?: SelectListItem[] | undefined;
  selected: string[];
  onChange: (v: string[]) => void;
  onAdd: () => void;
}

export function MultiSelectInput({
  options,
  listItems,
  selected,
  onChange,
  onAdd,
}: MultiSelectInputProps) {
  const t = useTableLabelFn();
  const { Button } = useTableComponents();
  const { locale } = useTableConfig();
  const [showDropdown, setShowDropdown] = useState(false);
  const displayOptions =
    listItems ?? options.map((o) => ({ value: o, label: { [locale]: o } }));
  const toggle = (opt: string) => {
    if (selected.includes(opt)) onChange(selected.filter((v) => v !== opt));
    else onChange([...selected, opt]);
  };
  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setShowDropdown(!showDropdown)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setShowDropdown(false);
        }}
        aria-label={t("table.filters.aria.selectMulti")}
        aria-expanded={showDropdown}
        className="rounded-md border px-2 py-1 text-sm"
      >
        {selected.length > 0
          ? t("table.filters.selectedCount", { count: selected.length })
          : t("table.filters.selectMulti")}
      </button>
      {showDropdown && (
        <div
          className="absolute z-10 max-h-48 overflow-y-auto rounded-md border bg-background shadow-lg"
          role="listbox"
          aria-label={t("table.filters.aria.selectMulti")}
        >
          {displayOptions.map((it) => {
            const value = typeof it === "string" ? it : it.value;
            const label =
              getListItemLabel(
                Array.isArray(listItems) ? listItems : undefined,
                value,
              ) ??
              (typeof it === "string" ? it : (it.label?.[locale] ?? value));
            return (
              <label
                key={value}
                className="flex items-center gap-2 p-2 hover:bg-surface cursor-pointer text-sm"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(value)}
                  onChange={() => toggle(value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      toggle(value);
                    }
                  }}
                />
                {label}
              </label>
            );
          })}
          <div className="border-t p-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowDropdown(false);
                onAdd();
              }}
            >
              {t("table.filters.selectMultiDone")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
